import { MovementArticle, Order, UpdateOrderPayload } from "types";
import { hasMultipleObservationValues, normalizeObservationInput } from "helpers/stockUnitObservationSelection";

export const MICROCONTROL_CLIENT_ID = 44;

const getReservedStockUnitIds = (article: MovementArticle): number[] => {
  const units = article.observation_preview_units ?? [];
  const ids = units.map((unit) => Number(unit.id));
  const articleLocationId = article.article_locations_id ?? article.article_locations?.id;

  if (
    !Number.isInteger(Number(article.amount)) ||
    ids.length !== Number(article.amount) ||
    ids.some((id) => !Number.isInteger(id) || id <= 0) ||
    new Set(ids).size !== ids.length ||
    units.some((unit) => Number(unit.article_location_id) !== Number(articleLocationId))
  ) {
    throw new Error(
      `No se pudieron identificar todas las unidades reservadas del artículo ${article.code || article.article_id}. Recargá el pedido e intentá nuevamente.`,
    );
  }

  return ids;
};

export const getOperatorArticleObservation = (article: MovementArticle): string => {
  const selection = article.line_data?.stock_unit_selection;
  if (!selection || typeof selection !== "object" || Array.isArray(selection)) return "";
  if (
    !("stock_unit_ids" in selection) ||
    !Array.isArray(selection.stock_unit_ids) ||
    !("observation_overrides" in selection) ||
    !Array.isArray(selection.observation_overrides)
  ) return "";

  const reservedIds = article.observation_preview_units?.map((unit) => Number(unit.id)) ?? [];
  const selectedIds = selection.stock_unit_ids.map(Number);
  if (
    reservedIds.length !== Number(article.amount) ||
    selectedIds.length !== reservedIds.length ||
    new Set(selectedIds).size !== selectedIds.length ||
    selectedIds.some((id) => !reservedIds.includes(id))
  ) return "";

  const overrides = new Map<number, string>();
  for (const value of selection.observation_overrides) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return "";
    if (!("stock_unit_id" in value) || !("observation" in value)) return "";
    if (typeof value.observation !== "string") return "";
    overrides.set(Number(value.stock_unit_id), value.observation);
  }

  const observations = selectedIds.map((id) => overrides.get(id));
  if (
    observations.some((value) => !value) ||
    hasMultipleObservationValues(observations.map((observation) => ({ observation })))
  ) return "";
  return observations[0] ?? "";
};

export const buildOperatorPreparationPayload = (
  order: Order,
  observations: Record<string, string>,
  generalObservation: string,
): UpdateOrderPayload => {
  const selectedUnitIds = new Set<number>();

  return {
    associated_client_id: order.movement_order.associated_client_id ?? null,
    pallets: order.pallets ?? null,
    weight: order.weight ?? null,
    vehicle: order.vehicle ?? null,
    observation: generalObservation.trim() || null,
    articles: order.articles.map((article) => {
      const observation = normalizeObservationInput(observations[String(article.id)] ?? "");
      const previousObservation = getOperatorArticleObservation(article);
      const shouldUpdateObservation = Boolean(observation || previousObservation);
      const stockUnitIds = shouldUpdateObservation ? getReservedStockUnitIds(article) : [];

      if (stockUnitIds.some((id) => selectedUnitIds.has(id))) {
        throw new Error("Hay unidades reservadas repetidas entre artículos. Recargá el pedido e intentá nuevamente.");
      }
      stockUnitIds.forEach((id) => selectedUnitIds.add(id));

      return {
        movement_article_id: Number(article.id),
        article_id: article.article_id,
        article_location_id: article.article_locations_id ?? article.article_locations?.id ?? null,
        location_id: article.location_id ?? article.article_locations?.location_id ?? null,
        amount: article.amount,
        amount_per_container: article.amount_per_container,
        lote: article.lote ?? null,
        oc: article.oc ?? null,
        fecha_ingreso: article.fecha_ingreso ?? null,
        fecha_vencimiento: article.fecha_vencimiento ?? null,
        line_data: shouldUpdateObservation
          ? {
              ...(article.line_data ?? {}),
              stock_unit_selection: {
                stock_unit_ids: stockUnitIds,
                observation_overrides: stockUnitIds.map((stockUnitId) => ({
                  stock_unit_id: stockUnitId,
                  observation: observation || null,
                })),
              },
            }
          : article.line_data ?? null,
      };
    }),
  };
};
